import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, createRoutesFromElements, Route, Navigate } from "react-router-dom";
import { Providers } from "../providers";

// Eagerly loaded — public marketing pages and auth flow
import { MarketingHomePage } from "../pages/marketing";
import { Pricing } from "../pages/pricing";
import { ChangelogPage } from "../pages/changelog";
import { BlogPage, BlogPostPage } from "@/pages/blog";
import { ContactPage } from "@/pages/contact";
import { DemoHubPage } from "@/pages/DemoHubPage";
import { Apex25Page } from "@/pages/apex25/Apex25Page";
import { APEX25_LEGAL_PAGES } from "@/pages/apex25/industries";
import { apex25IndustryRoutes, MovedToApex25 } from "@/pages/apex25/industry-routes";
import { ErrorBoundary } from "@/components/custom/errorBoundary";
import { ProtectedRoute } from "@/pages/auth/ProtectedRoute";
import AuthCallback from "@/pages/auth/AuthCallback";
import SignInPage from "@/pages/auth/SignInPage";
import SignUpPage from "@/pages/auth/SignUpPage";
import AcceptInvitation from "@/pages/invitation/AcceptInvitation";
import { MarketingLayout } from "@/components/layout/MarketingLayout";
import { AppLayout } from "@/components/layout/AppLayout";

// Lazily loaded — authenticated app routes
const Dashboard = lazy(() => import("../pages/dashboard").then((m) => ({ default: m.Dashboard })));
const JobPosting = lazy(() => import("../pages/jobs").then((m) => ({ default: m.JobPosting })));
const JobDetails = lazy(() =>
  import("../pages/jobDetails").then((m) => ({ default: m.JobDetails }))
);
const Candidates = lazy(() =>
  import("@/pages/candidates").then((m) => ({ default: m.Candidates }))
);
const InterviewManagement = lazy(() =>
  import("../pages/interviewManagement").then((m) => ({ default: m.InterviewManagement }))
);
const Settings = lazy(() => import("@/pages/settings").then((m) => ({ default: m.Settings })));
const NotificationsPage = lazy(() =>
  import("@/pages/notifications").then((m) => ({ default: m.NotificationsPage }))
);
const Companies = lazy(() => import("@/pages/companies").then((m) => ({ default: m.Companies })));
const CompanyDetail = lazy(() =>
  import("@/pages/companies").then((m) => ({ default: m.CompanyDetail }))
);
const BillingSuccess = lazy(() =>
  import("@/pages/billing/success").then((m) => ({ default: m.BillingSuccess }))
);
const AIAgentChat = lazy(() => import("@/pages/agent"));
const OnboardingWizard = lazy(() => import("@/pages/admin/onboarding/OnboardingWizard"));
const CentralResumeDatabase = lazy(() => import("@/pages/resume-database"));
const DemoResultsPage = lazy(() => import("@/pages/apex25/demo/DemoResultsPage"));
const Apex25LegalRoute = lazy(() =>
  import("@/pages/apex25/legal/page").then((m) => ({ default: m.Apex25LegalRoute }))
);

const L = ({ children }: { children: ReactNode }) => (
  <Suspense fallback={null}>{children}</Suspense>
);

export const routes = createRoutesFromElements(
  <Route element={<Providers />}>
    <Route path="auth-callback" element={<AuthCallback />} />
    <Route path="sign-in/*" element={<SignInPage />} />
    <Route path="sign-up/*" element={<SignUpPage />} />
    <Route path="invitation/accept" element={<AcceptInvitation />} />
    {/* Apex 25 marketing page brings its own header and footer. On the Apex 25 marketing hosts
        it is served at "/" by routes/apex25-marketing.routes.tsx instead of this router. */}
    <Route
      path="apex25"
      element={
        <MovedToApex25>
          <Apex25Page />
        </MovedToApex25>
      }
    />
    {/* The Apex 25 legal set, served at tryapex25.com/<path> in production. The same documents
        cover theRecruit, so the old /terms and /privacy here come to them too (the server 301s
        production). */}
    {Object.keys(APEX25_LEGAL_PAGES).map((path) => (
      <Route
        key={path}
        path={`apex25${path}`}
        element={
          <MovedToApex25>
            <L>
              <Apex25LegalRoute />
            </L>
          </MovedToApex25>
        }
      />
    ))}
    <Route path="terms" element={<Navigate to="/apex25/terms" replace />} />
    <Route path="privacy" element={<Navigate to="/apex25/privacy" replace />} />
    {/* Apex 25 demo results (review + Ask AI) for the visitor who just took the interview on
        tryapex25.com. Handles its own sign-in redirect (Apex 25-branded) and skips onboarding. */}
    <Route
      path="demo/interview/:sessionId"
      element={
        <L>
          <DemoResultsPage />
        </L>
      }
    />
    {/* Apex 25 industry pages (e.g. /for-legal) bring the Apex 25 header and footer. */}
    {apex25IndustryRoutes().map(({ path, element }) => (
      <Route key={path} path={path} element={element} />
    ))}
    {/* Marketing/Public Routes - Use MarketingLayout */}
    <Route element={<MarketingLayout />}>
      <Route path="/" index element={<MarketingHomePage />} />
      <Route path="home" element={<MarketingHomePage />} />
      <Route path="demo" element={<DemoHubPage />} />
      <Route path="pricing" element={<Pricing />} />
      <Route path="blog" element={<BlogPage />} />
      <Route path="blog/:slug" element={<BlogPostPage />} />
      <Route path="contact" element={<ContactPage />} />
      <Route path="changelog" element={<ChangelogPage />} />
    </Route>

    {/* Authenticated App Routes - Use AppLayout */}
    <Route element={<AppLayout />}>
      <Route
        path="dashboard"
        element={
          <ProtectedRoute>
            <L>
              <Dashboard />
            </L>
          </ProtectedRoute>
        }
      />

      {/* Notifications route */}
      <Route
        path="notifications"
        element={
          <ProtectedRoute>
            <L>
              <NotificationsPage />
            </L>
          </ProtectedRoute>
        }
      />

      {/* Companies route */}
      <Route path="companies">
        <Route
          index
          element={
            <ProtectedRoute>
              <L>
                <Companies />
              </L>
            </ProtectedRoute>
          }
        />
        <Route
          path=":companyId"
          element={
            <ProtectedRoute>
              <L>
                <CompanyDetail />
              </L>
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Job routes - Protected */}
      <Route path="jobs">
        <Route
          index
          element={
            <ProtectedRoute>
              <L>
                <JobPosting />
              </L>
            </ProtectedRoute>
          }
        />
        <Route
          path=":jobId"
          element={
            <ProtectedRoute>
              <L>
                <JobDetails />
              </L>
            </ProtectedRoute>
          }
          errorElement={<ErrorBoundary />}
        />
      </Route>

      <Route path="candidates">
        <Route
          path=":jobId"
          element={
            <ProtectedRoute>
              <L>
                <Candidates />
              </L>
            </ProtectedRoute>
          }
        />
      </Route>

      <Route
        path="settings"
        element={
          <ProtectedRoute>
            <L>
              <Settings />
            </L>
          </ProtectedRoute>
        }
      />

      <Route
        path="agent"
        element={
          <ProtectedRoute>
            <L>
              <AIAgentChat />
            </L>
          </ProtectedRoute>
        }
      />

      <Route
        path="resume-database"
        element={
          <ProtectedRoute>
            <L>
              <CentralResumeDatabase />
            </L>
          </ProtectedRoute>
        }
      />
    </Route>

    {/* Canonical onboarding route. Old setup URLs remain redirects for saved links. */}
    <Route path="org-setup">
      <Route index element={<Navigate to="onboarding" replace />} />
      <Route path="signup" element={<Navigate to="../onboarding" replace />} />
      <Route path="complete-profile" element={<Navigate to="../onboarding" replace />} />
      <Route path="create-organization" element={<Navigate to="../onboarding" replace />} />
      {/* Redirect old flagged-companies route to new tab-based route */}
      <Route path="flagged-companies" element={<Navigate to="/companies?tab=flagged" replace />} />
      <Route
        path="onboarding"
        element={
          <ProtectedRoute skipOnboardingCheck>
            <L>
              <OnboardingWizard />
            </L>
          </ProtectedRoute>
        }
      />
    </Route>
    {/* Legacy redirect: old recruiter path → canonical onboarding */}
    <Route path="admin" element={<Navigate to="/org-setup/onboarding" replace />} />

    {/* Other Routes - Decide on layout basis */}
    <Route path="billing">
      <Route
        path="success"
        element={
          <L>
            <BillingSuccess />
          </L>
        }
      />
    </Route>

    <Route path="interviewManagement">
      <Route
        index
        element={
          <L>
            <InterviewManagement />
          </L>
        }
      />
    </Route>

    {/* Catch-all route for 404s */}
    <Route path="*" element={<ErrorBoundary />} errorElement={<ErrorBoundary />} />
  </Route>
);

export const router = createBrowserRouter(routes);
